import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SparkleIcon as Sparkle, RobotIcon as Robot, CircleNotchIcon as CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * Inline editor for a kennel's "About" / bio field.
 *
 * Includes a one-click "Generate with AI" button that calls
 * /api/ai/kennel-bio (Claude Haiku via Emergent LLM Key) and pre-fills
 * the textarea so the kennel owner can fine-tune the output before saving.
 *
 * Saves via PUT /api/breeders/{id}.
 */
export default function BreederBioEditor({ breeder, open, onOpenChange, onSaved }) {
  const [bio, setBio] = useState(breeder?.bio || "");
  const [tone, setTone] = useState("warm");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset bio when dialog re-opens for a different breeder.
  React.useEffect(() => {
    if (open) setBio(breeder?.bio || "");
  }, [open, breeder?.id, breeder?.bio]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/ai/kennel-bio", {
        breeder_id: breeder.id,
        tone,
      });
      setBio(r.data?.bio || "");
      toast.success("AI draft generated — tweak it to match your voice");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: breeder.name,
        kennel_name: breeder.kennel_name || "",
        location: breeder.location || "",
        contact: breeder.contact || "",
        bio: (bio || "").trim(),
      };
      const r = await api.put(`/breeders/${breeder.id}`, payload);
      toast.success("Kennel bio saved");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="breeder-bio-editor">
        <DialogHeader>
          <DialogTitle className="font-spectral font-light text-3xl tracking-tight">Edit kennel bio</DialogTitle>
        </DialogHeader>
        <div className="gold-hairline -mt-1 mb-2" />

        <div className="space-y-4">
          <div className="relative rounded-xl border border-gold/40 bg-cream p-4 overflow-hidden">
            <span aria-hidden="true" className="absolute top-2 right-3 text-gold/55 text-base font-spectral leading-none">✦</span>
            <div className="flex items-center gap-2 mb-2">
              <Robot size={16} weight="duotone" className="text-[#8A6A00]" />
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#8A6A00]">
                AI assistant
              </p>
            </div>
            <p className="text-[12.5px] text-[#5C4A00] mb-3 leading-relaxed">
              Generate a 200-word About paragraph from your kennel data
              (name, location, dogs registered, breeds). You stay in
              control — the draft fills the editor below for you to
              edit.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">Tone</Label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="text-[13px] border border-gold/50 rounded-md px-2 py-1 bg-white focus-gold"
                data-testid="breeder-bio-tone-select"
              >
                <option value="warm">Warm &amp; welcoming</option>
                <option value="professional">Strictly professional</option>
                <option value="working">Working / serious</option>
                <option value="show">Show ring</option>
                <option value="family">Family / hobby</option>
              </select>
              <Button
                type="button"
                onClick={generate}
                disabled={generating}
                size="sm"
                className="ml-auto bg-gold-gradient hover:opacity-95 text-ink font-semibold rounded-full px-4 border-0 shadow-[0_4px_14px_rgba(201,162,39,0.3)]"
                data-testid="breeder-bio-generate-btn"
              >
                {generating ? (
                  <>
                    <CircleNotch size={13} className="animate-spin mr-1.5" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkle size={13} weight="fill" className="mr-1.5" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">
              Bio
            </Label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={9}
              className="mt-1 rounded-lg leading-relaxed"
              placeholder="Tell visitors about your breeding philosophy, decade-by-decade focus, championship record, health-testing posture, and what your kennel stands for…"
              data-testid="breeder-bio-textarea"
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1.5">
              {bio.length} characters · ~{Math.max(1, Math.round((bio || "").split(/\s+/).filter(Boolean).length))} words
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D]"
            data-testid="breeder-bio-save-btn"
          >
            {saving ? "Saving…" : "Save bio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
